import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import GameLeadProp from '../propTypes/gameLeadProp';
import CountryProp from '../../../commonComponents/forms/Address/CountryProp';
import { compose, LEAD_CONDITIONS } from '../../../services/utilityFunctions';
import withCSRF from '../../../commonComponents/withCSRF';
import translate from '../../../commonComponents/translate';
import Form from '../../../commonComponents/forms/Form';
import FormFooter from '../../../commonComponents/forms/FormFooter';
import FormHeader from '../../../commonComponents/forms/FormHeader';
import FormInput from '../../../commonComponents/forms/FormInput';
import Request from '../../../services/request';
import Notifications from '../../../services/notifications';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';

function LeadForm({ t, _csrf, isEdit, lead, countries }) {
  const { loggedUser } = useContext(LoggedUserContext);

  const [state, setState] = useState({
    user: lead.user._id,
    game: lead.game,
    condition: lead.condition,
    owner: lead.owner ? lead.owner._id : '',
    company: lead.company || '',
    image: lead.image,
    country: lead.country ? lead.country.code : '',
    endDate: lead.endDate,
  });

  const validationRules = {
    condition: { required: true },
    company: { required: true },
    endDate: { required: true },
  };

  const handleSubmit = () => {
    const params = {
      ...state,
      owner: state.owner || undefined,
      country: state.country
        ? countries.find(c => c.code === state.country)
        : undefined,
    };
    if (isEdit) {
      params._id = lead._id;
    }

    const url = isEdit ? `/challenges/leads/${lead._id}` : '/challenges/leads';
    const requestResult = isEdit
      ? new Request(_csrf).put(url, params)
      : new Request(_csrf).post(url, params);
    const successMessage = isEdit ? t('success_update') : t('success_create');

    requestResult
      .done(({ data }) => {
        Notifications.showNotificationSuccess(t('success'), successMessage);
        window.location.href = `/challenges/${data.game}/leads`;
      })
      .fail(err => {
        const error = isEdit ? 'error_leadUpdate' : 'error_leadCreate';
        Notifications.showErrorFromRequest(err, error);
      });
  };

  const handleChange = field => value =>
    setState(prevState => ({ ...prevState, [field]: value }));

  const createInput = (field, typeParam) => {
    const type = typeParam || 'text';

    return (
      <FormInput
        id={field}
        name={field}
        value={state[field]}
        onChange={handleChange(field)}
        label={t(`leadDetails_${field}`)}
        placeholder={t(`leadDetails_${field}Placeholder`)}
        labelClassName="col-2"
        containerClassName="col-7"
        type={type}
        groupProps={{ className: 'row' }}
        inputProps={{ required: true }}
      />
    );
  };

  const createSelect = (field, items) => (
    <FormInput
      id={field}
      name={field}
      value={state[field]}
      onChange={handleChange(field)}
      label={t(`leadDetails_${field}`)}
      disabled={field === 'owner' && lead.user._id !== loggedUser._id}
      labelClassName="col-2"
      containerClassName="col-7"
      type={field === 'owner' ? 'users' : 'select2'}
      placeholder={
        field !== 'condition' ? t(`leadDetails_${field}Placeholder`) : undefined
      }
      groupProps={{ className: 'row' }}
      inputProps={{
        searchable: field !== 'condition',
        required: field === 'condition',
        items,
        transformItems:
          field === 'owner' && loggedUser._id === lead.user._id
            ? data => data.filter(u => u.id !== loggedUser._id)
            : null,
      }}
    />
  );

  return (
    <Form
      className="m-form m-form--fit m-form--label-align-right"
      id="leadForm"
      submitMethod={handleSubmit}
      validationRules={validationRules}
    >
      {submitHandler => (
        <>
          <div className="m-portlet__body">
            <FormHeader title={t('info')} />
            {createInput('company')}
            {createSelect(
              'country',
              countries.map(c => ({
                id: c.code,
                text: `${c.flagEmoji} ${c.name}`,
              })),
            )}
            {createSelect('owner')}
            {createSelect(
              'condition',
              LEAD_CONDITIONS.map(c => ({
                id: c,
                text: t(`challengeDetails_leadCondition_${c}`),
              })),
            )}
            {createInput('endDate', 'date')}
          </div>
          <FormFooter submitHandler={submitHandler} />
        </>
      )}
    </Form>
  );
}

LeadForm.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  isEdit: PropTypes.bool.isRequired,
  lead: PropTypes.shape(GameLeadProp.propType).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
};

export default compose(
  withCSRF,
  translate,
)(LeadForm);
